import { apiClient } from './client';

/**
 * Notifications API client — backend `apps/chat-server/src/modules/notifications/notifications.routes.ts`.
 *
 * dual-trail (DB inbox + 실시간 SSE):
 *   - 본 모듈 = DB inbox (catch-up · cursor pagination · mark-read · count)
 *   - 실시간 = useSSE('me') 의 onNotification callback (`types/notification#NotificationEvent`)
 *
 * 두 representation 의 차이 (의도):
 *   - DTO (REST 응답): `id` field — backend serializeNotification 의 결과
 *   - Event (SSE payload): `notificationId` field — SSE envelope 의 routing key 와 자연스러운 명명
 *   client 가 NotificationDto 를 받으면 store 등에서 변환 없이 직접 사용.
 */

export interface NotificationDto {
  id: string;
  type: string;
  title: string;
  source: string;
  sourceId: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  notifications: NotificationDto[];
  nextCursor: string | null;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

export async function fetchUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
  return data.unreadCount;
}

export async function fetchNotifications(params?: {
  isRead?: boolean;
  limit?: number;
  cursor?: string;
}): Promise<NotificationListResponse> {
  const { data } = await apiClient.get<NotificationListResponse>('/notifications', {
    params: {
      ...(params?.isRead !== undefined ? { isRead: String(params.isRead) } : {}),
      ...(params?.limit ? { limit: params.limit } : {}),
      ...(params?.cursor ? { cursor: params.cursor } : {}),
    },
  });
  // 방어 가드(additive) — notifications 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return { ...data, notifications: Array.isArray(data?.notifications) ? data.notifications : [] };
}

export async function markAsRead(id: string): Promise<NotificationDto> {
  const { data } = await apiClient.post<{ notification: NotificationDto }>(
    `/notifications/${id}/read`,
  );
  return data.notification;
}

export async function readAll(): Promise<{ updated: number }> {
  const { data } = await apiClient.post<{ updated: number }>('/notifications/read-all');
  return data;
}

export async function deleteNotification(id: string): Promise<void> {
  await apiClient.delete(`/notifications/${id}`);
}
